import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Camera, Upload, Trash2, Copy, Share2, Download, Plus, X, LogOut, PawPrint,
  Syringe, FileText, ShieldAlert, Clock, Sparkles, Lock, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useUserAuth } from "@/contexts/UserAuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const DOC_CATEGORIES = ["Blood Reports", "Prescriptions", "X-rays", "Medical Bills", "Other"];

const FUTURE_SERVICES = [
  { icon: PawPrint, label: "Home Sample Collection", desc: "Vet lab tests at your door" },
  { icon: Syringe, label: "Vet Consultation", desc: "Video and in-person appointments" },
  { icon: ShieldAlert, label: "Health Camps", desc: "Free community check-ups" },
  { icon: FileText, label: "Insurance", desc: "Coverage for accidents & illness" },
  { icon: Sparkles, label: "Travel Verification", desc: "Airline / boarding readiness" },
  { icon: Clock, label: "Wellness Plans", desc: "Preventive care subscriptions" },
];

// Completeness: which fields to weight
const COMPLETENESS_FIELDS = [
  { key: "pet.photo", label: "Pet photo" },
  { key: "pet.name", label: "Pet name" },
  { key: "pet.species", label: "Species" },
  { key: "pet.breed", label: "Breed" },
  { key: "pet.weight", label: "Weight" },
  { key: "owner.name", label: "Owner name" },
  { key: "owner.mobile", label: "Mobile number" },
  { key: "owner.email", label: "Email" },
  { key: "emergency.allergies", label: "Allergies" },
  { key: "emergency.contact", label: "Emergency contact" },
  { key: "__vaccinations", label: "Vaccination records" },
  { key: "__documents", label: "Medical documents" },
];

const getField = (obj, path) => path.split(".").reduce((a, k) => (a ? a[k] : undefined), obj);

export default function PetRecord() {
  const { user, loading, logout } = useUserAuth();
  const [records, setRecords] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/pet-records/me").then((r) => {
      setRecords(r.data);
      setActive(r.data[0] || null);
    });
  }, [user]);

  if (loading) {
    return <div className="p-20 text-center text-brand-soft/60" data-testid="pet-record-loading">Loading…</div>;
  }

  if (!user) return <SignInGate />;

  if (records.length === 0) {
    return <EmptyState user={user} onLogout={logout} onCreated={(r) => { setRecords([r]); setActive(r); }} busy={busy} setBusy={setBusy} />;
  }

  const reloadActive = async () => {
    if (!active) return;
    const r = await api.get(`/pet-records/${active.id}`);
    setActive(r.data);
    setRecords((rs) => rs.map((x) => (x.id === r.data.id ? r.data : x)));
  };

  return (
    <div data-testid="pet-record-page">
      {/* Header */}
      <div className="bg-brand-ivory border-b border-brand-maroon/10">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-8 flex items-center justify-between gap-4">
          <div>
            <div className="eyebrow mb-1">My Furshala</div>
            <h1 className="font-serif text-2xl sm:text-3xl text-brand-soft">Pet Record</h1>
          </div>
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-brand-maroon/10" />}
            <div className="hidden sm:block text-sm text-brand-soft/70">{user.name || user.email}</div>
            <button onClick={logout} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-brand-maroon/10 hover:border-brand-maroon/30" data-testid="user-logout">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Record selector (multi-pet) */}
      {records.length > 1 && (
        <div className="max-w-6xl mx-auto px-6 sm:px-12 pt-6 flex flex-wrap gap-2" data-testid="record-tabs">
          {records.map((r) => (
            <button key={r.id} onClick={() => setActive(r)} className={`text-sm px-4 py-1.5 rounded-full border ${active?.id === r.id ? "bg-brand-maroon text-brand-cream border-brand-maroon" : "border-brand-maroon/20 hover:border-brand-maroon/50"}`}>
              {r.pet?.name || "Unnamed pet"}
            </button>
          ))}
        </div>
      )}

      {active && (
        <RecordEditor record={active} onChange={reloadActive} onDelete={async () => {
          if (!window.confirm("Delete this pet record permanently?")) return;
          await api.delete(`/pet-records/${active.id}`);
          const remaining = records.filter((x) => x.id !== active.id);
          setRecords(remaining);
          setActive(remaining[0] || null);
          toast.success("Record deleted");
        }} onAddPet={async () => {
          const r = await api.post("/pet-records", { pet: { name: "New Pet" } });
          setRecords((rs) => [r.data, ...rs]);
          setActive(r.data);
        }} />
      )}
    </div>
  );
}

/* ---------------- Sign-in gate ---------------- */
function SignInGate() {
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6" data-testid="pet-record-signin">
      <div className="max-w-md w-full bg-white rounded-3xl border border-brand-maroon/10 p-8 sm:p-10 text-center shadow-[0_20px_60px_-20px_rgba(122,42,45,0.15)]">
        <div className="w-14 h-14 rounded-2xl bg-brand-maroon/10 text-brand-maroon flex items-center justify-center mx-auto mb-5">
          <PawPrint className="w-6 h-6" />
        </div>
        <h1 className="font-serif text-3xl text-brand-soft">Your pet&apos;s lifelong record.</h1>
        <p className="mt-3 text-sm text-brand-soft/70 leading-relaxed">
          A private, secure medical record for every pet. Vaccinations, prescriptions, weight — one place, forever yours.
        </p>
        <button
          onClick={startGoogleLogin}
          className="mt-7 w-full inline-flex items-center justify-center gap-3 bg-brand-maroon text-brand-cream px-6 py-3.5 rounded-full text-sm font-medium hover:bg-brand-terracotta transition-colors"
          data-testid="google-login-btn"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </button>
        <p className="mt-4 text-xs text-brand-soft/50">By continuing you agree to our <Link className="underline" to="/privacy-policy">privacy policy</Link>.</p>
      </div>
    </div>
  );
}

/* ---------------- Empty state (no records yet) ---------------- */
function EmptyState({ user, onLogout, onCreated, busy, setBusy }) {
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/pet-records", {
        pet: { name: "" },
        owner: { name: user.name || "", email: user.email || "" },
      });
      onCreated(r.data);
      toast.success("Record created — fill in your pet's details");
    } catch (e) {
      toast.error("Could not create record");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="pet-record-empty">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-2xl bg-brand-mustard/20 flex items-center justify-center mx-auto mb-5">
          <PawPrint className="w-7 h-7 text-brand-maroon" />
        </div>
        <h1 className="font-serif text-3xl text-brand-soft">Add your first pet.</h1>
        <p className="mt-2 text-sm text-brand-soft/70">Start your pet&apos;s lifelong Furshala record — it&apos;s free and private.</p>
        <button onClick={create} disabled={busy} className="btn-primary-furshala inline-flex items-center gap-2 mt-6 disabled:opacity-60" data-testid="create-record-btn">
          <Plus className="w-4 h-4" /> Create Pet Record
        </button>
        <button onClick={onLogout} className="block mx-auto mt-4 text-xs text-brand-soft/50 hover:text-brand-maroon">Sign out</button>
      </div>
    </div>
  );
}

/* ---------------- Record Editor ---------------- */
function RecordEditor({ record, onChange, onDelete, onAddPet }) {
  const [pet, setPet] = useState(record.pet || {});
  const [owner, setOwner] = useState(record.owner || {});
  const [emergency, setEmergency] = useState(record.emergency || {});
  useEffect(() => {
    setPet(record.pet || {});
    setOwner(record.owner || {});
    setEmergency(record.emergency || {});
  }, [record.id]);

  const saveSection = async (section, value) => {
    try {
      await api.put(`/pet-records/${record.id}`, { [section]: value });
      onChange();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const completeness = useMemo(() => {
    const missing = [];
    let filled = 0;
    for (const f of COMPLETENESS_FIELDS) {
      let has = false;
      if (f.key === "__vaccinations") has = (record.vaccinations || []).length > 0;
      else if (f.key === "__documents") has = (record.documents || []).length > 0;
      else has = !!getField(record, f.key);
      if (has) filled += 1;
      else missing.push(f.label);
    }
    return { pct: Math.round((filled / COMPLETENESS_FIELDS.length) * 100), missing };
  }, [record]);

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-12 py-8 sm:py-12 grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="record-editor">
      <div className="lg:col-span-2 space-y-8">
        {/* Pet Info */}
        <Section title="Pet Information" testId="section-pet-info">
          <PetPhotoUploader record={record} value={pet.photo} onChange={(v) => { setPet({ ...pet, photo: v }); saveSection("pet", { ...pet, photo: v }); }} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <Field label="Pet name *" value={pet.name} onChange={(v) => setPet({ ...pet, name: v })} onBlur={() => saveSection("pet", pet)} testId="input-pet-name" />
            <SelectField label="Species" value={pet.species} onChange={(v) => { const p = { ...pet, species: v }; setPet(p); saveSection("pet", p); }} options={["Dog","Cat"]} testId="input-species" />
            <Field label="Breed" value={pet.breed} onChange={(v) => setPet({ ...pet, breed: v })} onBlur={() => saveSection("pet", pet)} testId="input-breed" />
            <SelectField label="Gender" value={pet.gender} onChange={(v) => { const p = { ...pet, gender: v }; setPet(p); saveSection("pet", p); }} options={["Male","Female"]} testId="input-gender" />
            <Field label="Date of birth" type="date" value={pet.dob} onChange={(v) => setPet({ ...pet, dob: v })} onBlur={() => saveSection("pet", pet)} testId="input-dob" />
            <Field label="Weight" placeholder="e.g. 8 kg" value={pet.weight} onChange={(v) => setPet({ ...pet, weight: v })} onBlur={() => saveSection("pet", pet)} testId="input-weight" />
            <Field label="Colour" value={pet.colour} onChange={(v) => setPet({ ...pet, colour: v })} onBlur={() => saveSection("pet", pet)} testId="input-colour" />
            <Field label="Microchip # (optional)" value={pet.microchip} onChange={(v) => setPet({ ...pet, microchip: v })} onBlur={() => saveSection("pet", pet)} testId="input-microchip" />
          </div>
        </Section>

        {/* Owner Info */}
        <Section title="Owner Information" testId="section-owner">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Owner name" value={owner.name} onChange={(v) => setOwner({ ...owner, name: v })} onBlur={() => saveSection("owner", owner)} testId="input-owner-name" />
            <Field label="Mobile number" value={owner.mobile} onChange={(v) => setOwner({ ...owner, mobile: v })} onBlur={() => saveSection("owner", owner)} testId="input-owner-mobile" />
            <Field label="Email" value={owner.email} onChange={(v) => setOwner({ ...owner, email: v })} onBlur={() => saveSection("owner", owner)} testId="input-owner-email" />
            <Field label="City" value={owner.city} onChange={(v) => setOwner({ ...owner, city: v })} onBlur={() => saveSection("owner", owner)} testId="input-owner-city" />
          </div>
        </Section>

        {/* Vaccinations */}
        <VaccinationsSection record={record} onChange={onChange} />

        {/* Documents */}
        <DocumentsSection record={record} onChange={onChange} />

        {/* Emergency */}
        <Section title="Emergency Information" testId="section-emergency">
          <div className="space-y-3">
            <TextArea label="Allergies" value={emergency.allergies} onChange={(v) => setEmergency({ ...emergency, allergies: v })} onBlur={() => saveSection("emergency", emergency)} testId="input-allergies" />
            <TextArea label="Current medications" value={emergency.medications} onChange={(v) => setEmergency({ ...emergency, medications: v })} onBlur={() => saveSection("emergency", emergency)} testId="input-medications" />
            <Field label="Emergency contact (name + phone)" value={emergency.contact} onChange={(v) => setEmergency({ ...emergency, contact: v })} onBlur={() => saveSection("emergency", emergency)} testId="input-emergency-contact" />
          </div>
        </Section>

        {/* Activity timeline */}
        <Section title="Activity Timeline" testId="section-activity">
          {(record.activity || []).length === 0 ? (
            <div className="text-sm text-brand-soft/60 italic">Every save, vaccination and upload will appear here.</div>
          ) : (
            <ol className="space-y-3">
              {[...(record.activity || [])].reverse().map((a) => (
                <li key={a.id} className="flex items-start gap-3">
                  <div className="mt-1 w-2 h-2 rounded-full bg-brand-maroon flex-shrink-0" />
                  <div>
                    <div className="text-sm text-brand-soft">{a.description}</div>
                    <div className="text-xs text-brand-soft/50">{new Date(a.timestamp).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Section>

        {/* Danger zone */}
        <div className="flex justify-between items-center">
          <button onClick={onAddPet} className="text-xs inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-mustard/20 text-brand-maroon hover:bg-brand-mustard/30" data-testid="add-pet-btn">
            <Plus className="w-3.5 h-3.5" /> Add another pet
          </button>
          <button onClick={onDelete} className="text-xs inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-50 text-red-700 hover:bg-red-100" data-testid="delete-record-btn">
            <Trash2 className="w-3.5 h-3.5" /> Delete this record
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside className="space-y-6">
        <CompletenessCard pct={completeness.pct} missing={completeness.missing} />
        <ShareCard record={record} />
        <FutureServicesCard />
      </aside>
    </div>
  );
}

/* ---------------- Section wrapper ---------------- */
const Section = ({ title, children, testId }) => (
  <section className="bg-white rounded-3xl border border-brand-maroon/10 p-6 sm:p-7" data-testid={testId}>
    <h2 className="font-serif text-xl sm:text-2xl text-brand-soft mb-5">{title}</h2>
    {children}
  </section>
);

const Field = ({ label, value, onChange, onBlur, type = "text", placeholder, testId }) => (
  <label className="block space-y-1">
    <div className="text-[11px] uppercase tracking-widest text-brand-soft/60">{label}</div>
    <input
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40"
      data-testid={testId}
    />
  </label>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <label className="block space-y-1">
    <div className="text-[11px] uppercase tracking-widest text-brand-soft/60">{label}</div>
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" data-testid={testId}>
      <option value="">Select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

const TextArea = ({ label, value, onChange, onBlur, testId }) => (
  <label className="block space-y-1">
    <div className="text-[11px] uppercase tracking-widest text-brand-soft/60">{label}</div>
    <textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}
      className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40"
      data-testid={testId}
    />
  </label>
);

/* ---------------- Pet photo uploader (uses admin/upload endpoint isn't for users; falls back to public) ---------------- */
function PetPhotoUploader({ record, value, onChange }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Photo must be under 10MB"); return; }
    setBusy(true);
    try {
      // Use documents endpoint with category 'PetPhoto' so it stores + returns URL
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(`/pet-records/${record.id}/documents?category=PetPhoto`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `/api/files/${r.data.storage_path}`;
      onChange(url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24 rounded-2xl bg-brand-ivory overflow-hidden border border-brand-maroon/10 flex-shrink-0" data-testid="pet-photo">
        {value ? <img src={fileUrl(value)} alt="pet" className="w-full h-full object-cover" /> : <PawPrint className="w-8 h-8 text-brand-maroon/40 absolute inset-0 m-auto" />}
      </div>
      <div>
        <button type="button" onClick={pick} disabled={busy} className="text-xs inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-maroon text-brand-cream hover:bg-brand-terracotta disabled:opacity-60" data-testid="upload-pet-photo">
          <Camera className="w-3.5 h-3.5" /> {value ? "Change photo" : "Upload photo"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} />
        <div className="text-xs text-brand-soft/50 mt-1.5">JPG or PNG, up to 10MB</div>
      </div>
    </div>
  );
}

/* ---------------- Vaccinations Section ---------------- */
function VaccinationsSection({ record, onChange }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", date_given: "", next_due: "" });
  const add = async () => {
    if (!form.name || !form.date_given) { toast.error("Vaccine name and date are required"); return; }
    try {
      await api.post(`/pet-records/${record.id}/vaccinations`, form);
      setForm({ name: "", date_given: "", next_due: "" });
      setAdding(false);
      onChange();
      toast.success("Vaccination added");
    } catch (e) {
      toast.error("Save failed");
    }
  };
  const remove = async (id) => {
    await api.delete(`/pet-records/${record.id}/vaccinations/${id}`);
    onChange();
  };
  return (
    <Section title="Vaccination Records" testId="section-vaccinations">
      {(record.vaccinations || []).length === 0 && !adding && (
        <div className="text-sm text-brand-soft/60 italic mb-4">No vaccinations added yet.</div>
      )}
      <ul className="space-y-2">
        {(record.vaccinations || []).map((v) => (
          <li key={v.id} className="flex items-center justify-between gap-3 bg-brand-ivory rounded-xl px-4 py-3" data-testid={`vaccination-row-${v.id}`}>
            <div>
              <div className="text-sm font-medium text-brand-soft">{v.name}</div>
              <div className="text-xs text-brand-soft/60">Given {v.date_given}{v.next_due ? ` · Next due ${v.next_due}` : ""}</div>
            </div>
            <button onClick={() => remove(v.id)} className="text-xs text-red-700 hover:underline" data-testid={`del-vaccination-${v.id}`}>Remove</button>
          </li>
        ))}
      </ul>
      {adding ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 bg-brand-ivory rounded-xl p-4" data-testid="vaccination-form">
          <Field label="Vaccine name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Rabies" testId="vac-name" />
          <Field label="Date given" type="date" value={form.date_given} onChange={(v) => setForm({ ...form, date_given: v })} testId="vac-date" />
          <Field label="Next due" type="date" value={form.next_due} onChange={(v) => setForm({ ...form, next_due: v })} testId="vac-next-due" />
          <div className="sm:col-span-3 flex gap-2">
            <button onClick={add} className="btn-primary-furshala text-sm py-2" data-testid="save-vaccination">Save</button>
            <button onClick={() => setAdding(false)} className="text-sm text-brand-soft/60">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="mt-4 inline-flex items-center gap-1.5 text-sm text-brand-maroon hover:underline" data-testid="add-vaccination-btn">
          <Plus className="w-4 h-4" /> Add Vaccination
        </button>
      )}
    </Section>
  );
}

/* ---------------- Documents Section ---------------- */
function DocumentsSection({ record, onChange }) {
  const inputRef = useRef();
  const [category, setCategory] = useState("Blood Reports");
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/pet-records/${record.id}/documents?category=${encodeURIComponent(category)}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange();
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    await api.delete(`/pet-records/${record.id}/documents/${id}`);
    onChange();
  };

  const grouped = useMemo(() => {
    const g = {};
    (record.documents || []).filter((d) => d.category !== "PetPhoto").forEach((d) => {
      g[d.category] = g[d.category] || [];
      g[d.category].push(d);
    });
    return g;
  }, [record.documents]);

  return (
    <Section title="Medical Documents" testId="section-documents">
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-brand-ivory rounded-xl p-3">
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-white rounded-lg px-3 py-2 text-sm border border-brand-maroon/10" data-testid="doc-category">
          {DOC_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary-furshala inline-flex items-center gap-1.5 text-sm py-2 disabled:opacity-60" data-testid="upload-doc-btn">
          <Upload className="w-4 h-4" /> Upload
        </button>
        <input ref={inputRef} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={upload} />
        <div className="text-xs text-brand-soft/50">PDF, JPG, PNG · up to 10MB · max 20 files</div>
      </div>
      {Object.keys(grouped).length === 0 ? (
        <div className="text-sm text-brand-soft/60 italic">No documents yet. Upload prescriptions, reports, x-rays.</div>
      ) : (
        <div className="space-y-4">
          {DOC_CATEGORIES.filter((c) => grouped[c]?.length).map((c) => (
            <div key={c}>
              <div className="text-[11px] uppercase tracking-widest text-brand-soft/50 mb-2">{c}</div>
              <ul className="space-y-1.5">
                {grouped[c].map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 bg-brand-ivory rounded-lg px-3 py-2" data-testid={`doc-row-${d.id}`}>
                    <a href={fileUrl(`/api/files/${d.storage_path}`)} target="_blank" rel="noreferrer" className="text-sm text-brand-maroon hover:underline flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 flex-shrink-0" /> <span className="truncate">{d.file_name}</span>
                    </a>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-xs text-brand-soft/50">{new Date(d.uploaded_at).toLocaleDateString()}</div>
                      <button onClick={() => remove(d.id)} className="text-xs text-red-700 hover:underline">Remove</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ---------------- Completeness ---------------- */
function CompletenessCard({ pct, missing }) {
  return (
    <div className="bg-white rounded-3xl border border-brand-maroon/10 p-6" data-testid="completeness-card">
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-serif text-lg text-brand-soft">Record Completeness</div>
        <div className="font-serif text-2xl text-brand-maroon" data-testid="completeness-pct">{pct}%</div>
      </div>
      <div className="h-2 bg-brand-ivory rounded-full overflow-hidden">
        <div className="h-full bg-brand-maroon transition-all" style={{ width: `${pct}%` }} />
      </div>
      {missing.length > 0 && (
        <>
          <div className="text-xs text-brand-soft/60 mt-4 mb-2">Still missing:</div>
          <ul className="text-sm text-brand-soft/80 space-y-1">
            {missing.slice(0, 6).map((m) => (
              <li key={m} className="flex items-center gap-2 text-xs"><span className="w-1.5 h-1.5 rounded-full bg-brand-mustard" /> {m}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/* ---------------- Share ---------------- */
function ShareCard({ record }) {
  const link = `${window.location.origin}/p/${record.share_id}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `${record.pet?.name || "My pet"}'s record`, url: link }); } catch { /* cancelled */ }
    } else copy();
  };
  const downloadPdf = () => window.open(link + "?print=1", "_blank");
  return (
    <div className="bg-white rounded-3xl border border-brand-maroon/10 p-6" data-testid="share-card">
      <div className="font-serif text-lg text-brand-soft mb-3">Shareable Record</div>
      <div className="bg-brand-ivory rounded-xl px-3 py-2 text-xs text-brand-soft/70 break-all mb-3" data-testid="share-link">{link}</div>
      <div className="flex flex-wrap gap-2">
        <button onClick={copy} className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-mustard/20 text-brand-maroon hover:bg-brand-mustard/30" data-testid="copy-link"><Copy className="w-3.5 h-3.5" /> Copy</button>
        <button onClick={share} className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-mustard/20 text-brand-maroon hover:bg-brand-mustard/30" data-testid="share-link-btn"><Share2 className="w-3.5 h-3.5" /> Share</button>
        <button onClick={downloadPdf} className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-mustard/20 text-brand-maroon hover:bg-brand-mustard/30" data-testid="download-pdf"><Download className="w-3.5 h-3.5" /> Print / PDF</button>
      </div>
      <div className="text-[11px] text-brand-soft/50 mt-3 leading-relaxed">Public link shows only pet info + vaccinations. Documents and phone/email stay private.</div>
    </div>
  );
}

/* ---------------- Future services (disabled cards) ---------------- */
function FutureServicesCard() {
  return (
    <div className="bg-white rounded-3xl border border-brand-maroon/10 p-6" data-testid="future-services">
      <div className="font-serif text-lg text-brand-soft mb-1">Coming to Furshala</div>
      <div className="text-xs text-brand-soft/60 mb-4">All future services will auto-log to your pet&apos;s record.</div>
      <ul className="space-y-2">
        {FUTURE_SERVICES.map((s) => {
          const Icon = s.icon;
          return (
            <li key={s.label} className="flex items-center gap-3 bg-brand-ivory/60 rounded-xl px-3 py-2 opacity-70">
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-brand-soft/60" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-brand-soft font-medium truncate">{s.label}</div>
                <div className="text-[11px] text-brand-soft/50 truncate">{s.desc}</div>
              </div>
              <Lock className="w-3.5 h-3.5 text-brand-soft/40 flex-shrink-0" />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
